<template>
  <div
    class="base-vxe-table"
    :class="[
      `is-size-${normalizedSize}`,
      {
        'is-outer-border': showOuterBorder,
        'is-row-divider': showRowDivider,
        'is-col-divider': showColumnDivider,
        'is-column-draggable': columnDraggable,
        'has-row-selection': hasCheckboxSelection,
        'is-empty-or-loading': isEmptyOrLoadingContent,
      },
    ]"
    :style="tableStyleVars"
  >
    <vxe-table
      :id="tableId"
      :key="tableRenderKey"
      ref="tableRef"
      :data="dataSource"
      :loading="loading"
      :size="normalizedSize"
      :border="resolvedBorder"
      :show-overflow="showOverflow"
      :row-config="rowConfig"
      :column-config="columnConfig"
      :column-drag-config="columnDragConfig"
      :checkbox-config="checkboxConfig"
      :custom-config="customConfig"
      :tree-config="treeConfig"
      :scroll-x="scrollX"
      auto-resize
      @checkbox-change="handleCheckboxChange"
      @checkbox-all="handleCheckboxChange"
    >
      <vxe-column
        v-if="hasCheckboxSelection"
        type="checkbox"
        width="48"
        fixed="left"
        align="center"
        :resizable="false"
      />
      <vxe-column
        v-for="column in vxeColumns"
        :key="column.field"
        :field="column.field"
        :title="column.title"
        :width="column.width"
        :min-width="column.minWidth"
        :fixed="column.fixed"
        :align="column.align"
        :resizable="column.resizable"
        :tree-node="column.treeNode"
        :show-overflow="false"
        :class-name="column.cellClassName"
      >
        <template #default="{ row }">
          <slot
            v-if="column.isCustomSlot"
            :name="column.field"
            :record="row"
            :column="column"
          />
          <BaseVxeCellText
            v-else
            :text="formatCellText(row[column.field])"
            :rows="column.ellipsis.enabled ? column.ellipsis.rows : 0"
            :align="column.align ?? 'center'"
            :tooltip="column.ellipsis.tooltip"
          />
        </template>
      </vxe-column>
    </vxe-table>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, useSlots, watch } from 'vue'
import type { VxeTableInstance } from 'vxe-table'
import {
  DEFAULT_NON_DRAGGABLE_VXE_COLUMN_KEYS,
  type BaseVxeTableBorder,
  type BaseVxeTableProps,
} from '@/components/baseVxeTable/types'
import {
  getRowKeyValue,
  resolveRowKeyField,
} from '@/utils/baseVxeTableRowKey'
import {
  buildVxeTableId,
  clearVxeTableCustomStorage,
  formatVxeCellText,
  mapAntdColumnsToVxe,
} from '@/utils/vxeTableColumns'
import BaseVxeCellText from '@/components/baseVxeTable/BaseVxeCellText.vue'

defineOptions({
  name: 'BaseVxeTable',
})

const props = withDefaults(defineProps<BaseVxeTableProps>(), {
  dataSource: () => [],
  loading: false,
  /** 列顺序拖拽，默认开启；按需传 false 关闭 */
  columnDraggable: true,
  /** 列宽拖拽，默认开启 */
  columnResizable: true,
  /** 列宽/列顺序持久化到 localStorage，默认开启（需配合 persistKey） */
  persistColumns: true,
  /** 表格尺寸，medium 对齐 antd middle */
  size: 'medium',
  /** 表格外边框，默认关闭（对齐 antd 非 bordered 表格） */
  showOuterBorder: false,
  /** 横向行分割线，默认开启 */
  showRowDivider: true,
  /** 纵向列分割线，默认关闭 */
  showColumnDivider: false,
  /** 关闭 vxe 原生 title 提示，单元格溢出由 antd Tooltip 处理 */
  showOverflow: false,
  /** 禁止参与列顺序拖拽的列（如操作列） */
  nonDraggableColumnKeys: () => [...DEFAULT_NON_DRAGGABLE_VXE_COLUMN_KEYS],
  /** 暂无数据时内容区最小高度 */
  emptyMinHeight: 200,
  /** 加载动画距容器顶部的固定偏移（px），未传时按表头高度 + emptyMinHeight/2 计算 */
  loadingTopOffset: undefined,
})

const tableRef = ref<VxeTableInstance>()
const tableRenderKey = ref(0)
const isSyncingCheckbox = ref(false)
const slots = useSlots()

const customSlotFields = computed(() => {
  const slotNames = Object.keys(slots)
  return new Set(slotNames.filter((name) => name !== 'default'))
})

const formatCellText = formatVxeCellText

const normalizedSize = computed(() => (props.size === 'middle' ? 'medium' : props.size))

const TABLE_HEADER_HEIGHT: Record<string, number> = {
  medium: 47,
  middle: 47,
  small: 39,
  mini: 32,
}

const tableStyleVars = computed(() => {
  const headerHeight = TABLE_HEADER_HEIGHT[normalizedSize.value] ?? 47
  const loadingTop =
    props.loadingTopOffset ?? headerHeight + props.emptyMinHeight / 2

  return {
    '--base-vxe-empty-min-height': `${props.emptyMinHeight}px`,
    '--base-vxe-table-header-height': `${headerHeight}px`,
    '--base-vxe-loading-top': `${loadingTop}px`,
  }
})

const hasCheckboxSelection = computed(
  () => Boolean(props.rowSelection) && props.rowSelection?.type !== 'radio'
)

/** 无数据或加载中时，保证内容区最小高度（vxe 加载态不会加 is--empty） */
const isEmptyOrLoadingContent = computed(
  () => props.loading || !(props.dataSource?.length)
)

const resolvedRowKeyField = computed(() => resolveRowKeyField(props.rowKey))

const rowConfig = computed(() => {
  const config: { useKey: boolean; keyField?: string } = { useKey: true }
  if (resolvedRowKeyField.value) {
    config.keyField = resolvedRowKeyField.value
  }
  return config
})

const resolvedBorder = computed<BaseVxeTableBorder>(() => {
  if (props.border !== undefined) {
    return props.border
  }

  const { showOuterBorder, showRowDivider, showColumnDivider } = props

  if (!showOuterBorder && !showRowDivider && !showColumnDivider) {
    return 'none'
  }
  if (showColumnDivider) {
    return 'full'
  }
  if (showRowDivider) {
    return 'default'
  }
  if (showOuterBorder) {
    return 'outer'
  }
  return 'none'
})

const tableId = computed(() => {
  if (!props.persistKey) return undefined
  return buildVxeTableId(props.persistKey, props.userId ?? 'guest')
})

const nonDraggableKeys = computed(
  () => new Set(props.nonDraggableColumnKeys.map((key) => String(key)))
)

const vxeColumns = computed(() =>
  mapAntdColumnsToVxe(props.columns, {
    columnResizable: props.columnResizable,
    nonDraggableKeys: nonDraggableKeys.value,
  }).map((column) => {
    const isCustomSlot = customSlotFields.value.has(column.field)
    if (isCustomSlot) {
      return {
        ...column,
        isCustomSlot: true,
        cellClassName: 'col--full-content',
      }
    }
    return {
      ...column,
      isCustomSlot: false,
    }
  })
)

const columnConfig = computed(() => ({
  useKey: true,
  resizable: props.columnResizable,
  /** vxe 列顺序拖拽开关，需与 column-drag-config 配合 */
  drag: props.columnDraggable,
}))

const columnDragConfig = computed(() => {
  if (!props.columnDraggable) return undefined

  return {
    /** 同级列之间拖拽排序 */
    isPeerDrag: true,
    disabledMethod: ({ column }: { column?: { field?: string; type?: string } }) => {
      if (column?.type === 'checkbox') return true
      const field = column?.field
      if (!field) return false
      return nonDraggableKeys.value.has(field)
    },
  }
})

const checkboxConfig = computed(() => {
  if (!hasCheckboxSelection.value) return undefined

  const selectedRowKeys = props.rowSelection?.selectedRowKeys ?? []
  const getCheckboxProps = props.rowSelection?.getCheckboxProps

  return {
    checkRowKeys: [...selectedRowKeys],
    highlight: true,
    checkMethod: getCheckboxProps
      ? ({ row }: { row: Record<string, unknown> }) => !getCheckboxProps(row).disabled
      : undefined,
  }
})

const customConfig = computed(() => {
  if (!props.persistKey || !props.persistColumns || !tableId.value) return undefined

  return {
    storage: {
      resizable: props.columnResizable,
      sort: props.columnDraggable,
      fixed: true,
    },
  }
})

const scrollX = computed(() => ({
  enabled: true,
}))

const treeConfig = computed(() => props.treeConfig)

const emitRowSelectionChange = () => {
  const onChange = props.rowSelection?.onChange
  const $table = tableRef.value
  if (!onChange || !$table) return

  const selectedRows = $table.getCheckboxRecords() as Record<string, unknown>[]
  const selectedRowKeys = selectedRows.map((row) => getRowKeyValue(row, props.rowKey))
  onChange(selectedRowKeys, selectedRows)
}

const handleCheckboxChange = () => {
  if (isSyncingCheckbox.value) return
  emitRowSelectionChange()
}

const syncCheckboxSelection = () => {
  if (!hasCheckboxSelection.value) return

  const $table = tableRef.value
  if (!$table) return

  isSyncingCheckbox.value = true
  nextTick(() => {
    $table.clearCheckboxRow()
    const keys = props.rowSelection?.selectedRowKeys ?? []
    if (keys.length) {
      if (resolvedRowKeyField.value) {
        $table.setCheckboxRowKey(keys, true)
      } else if (typeof props.rowKey === 'function') {
        const rows = (props.dataSource ?? []).filter((row) =>
          keys.some((key) => String(key) === String(getRowKeyValue(row, props.rowKey)))
        )
        if (rows.length) {
          $table.setCheckboxRow(rows, true)
        }
      }
    }
    isSyncingCheckbox.value = false
  })
}

const resetColumnPrefs = () => {
  if (tableId.value) {
    clearVxeTableCustomStorage(tableId.value)
  }
  tableRenderKey.value += 1
}

watch(
  () => props.columns,
  () => {
    tableRenderKey.value += 1
  },
  { deep: true }
)

watch(
  () => [props.rowSelection?.selectedRowKeys, props.dataSource, props.rowKey] as const,
  () => {
    syncCheckboxSelection()
  },
  { deep: true }
)

watch(
  () => props.rowSelection,
  () => {
    if (hasCheckboxSelection.value) {
      syncCheckboxSelection()
    }
  },
  { deep: true, immediate: true }
)

defineExpose({
  resetColumnPrefs,
  tableRef,
  syncCheckboxSelection,
})
</script>
